import { useEffect, useMemo, useState } from 'react';

import { useParams } from 'react-router-dom';

import DeleteOutlineTwoToneIcon from '@mui/icons-material/DeleteOutlineTwoTone';
import EditTwoToneIcon from '@mui/icons-material/EditTwoTone';
import { Box, InputAdornment, Stack, Typography } from '@mui/material';
import {
  GridActionsCellItem,
  GridRenderCellParams,
  GridRowParams
} from '@mui/x-data-grid';
import usePublisher from 'api/usePublisher';
import { EPermissionAction, EPermissionGroup } from 'common/permissions.types';
import { MAX_USD_PRICE } from 'constants/constants';
import { useFormik } from 'formik';
import { permissionsUtil } from 'utils/permissionsUtil';
import * as Yup from 'yup';

import AcInput from 'components/AcInput/AcInput';
import Button from 'components/Button/Button';
import DataTable from 'components/DataTable/DataTable';
import FirstActionModal from 'components/FirstActionModal/FirstActionModal';

import { ThresholdData } from './Pricing.types';

const validationSchema = (existingRates: number[]) =>
  Yup.object().shape({
    maxRate: Yup.number()
      .required('Required')
      .min(0, 'Must be at least 0')
      .max(MAX_USD_PRICE, 'Must be 5000 or less')
      .test(
        'unique-maxRate',
        'This max rate already exists',
        (value) => value === undefined || !existingRates.includes(value)
      ),
    minGap: Yup.number()
      .required('Required')
      .max(0, 'Must be a negative number'),
    maxGap: Yup.number()
      .required('Required')
      .min(0, 'Must be a positive number')
  });

const ExchangeRateThreshold: React.FC<{ disabled?: boolean }> = ({
  disabled
}) => {
  const { publisherId } = useParams();
  const { getPublisherSettings, updateConversionThresholds } = usePublisher(
    publisherId || undefined
  );

  const [thresholds, setThresholds] = useState<ThresholdData[]>([]);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  useEffect(() => {
    if (!getPublisherSettings.isLoading && getPublisherSettings.data) {
      const existingThresholds =
        getPublisherSettings.data.priceCheckerConversionsThresholds || [];

      const hasMaxPriceThreshold = existingThresholds.some(
        (threshold: ThresholdData) => threshold.priceThreshold === MAX_USD_PRICE
      );

      const updatedThresholds = hasMaxPriceThreshold
        ? existingThresholds
        : [
            ...existingThresholds,
            {
              priceThreshold: MAX_USD_PRICE,
              negativeMaxDeviation: 0.1,
              positiveMaxDeviation: 0.1
            }
          ];

      setThresholds(updatedThresholds);
    }
  }, [getPublisherSettings.data, getPublisherSettings.isLoading]);

  const rows = useMemo(() => {
    return thresholds.map((threshold, index) => ({
      _id: index,
      priceRangeStart:
        index === 0 ? 0 : thresholds[index - 1]?.priceThreshold || 0,
      priceThreshold: threshold.priceThreshold,
      negativeMaxDeviation: (threshold.negativeMaxDeviation * 100).toFixed(0),
      positiveMaxDeviation: (threshold.positiveMaxDeviation * 100).toFixed(0)
    }));
  }, [thresholds]);

  const {
    values,
    handleChange,
    dirty,
    isValid,
    errors,
    touched,
    setFieldValue,
    handleSubmit,
    resetForm,
    handleBlur,
    setValues
  } = useFormik({
    initialValues: {
      minRate: '',
      maxRate: '',
      minGap: '',
      maxGap: ''
    },
    validationSchema: validationSchema(
      thresholds
        .filter((_, i) => i !== editingIndex)
        .map((t) => t.priceThreshold)
    ),
    onSubmit: () => {
      const newThreshold: ThresholdData = {
        priceThreshold: parseFloat(values.maxRate),
        negativeMaxDeviation: (parseFloat(values.minGap) / 100) * -1,
        positiveMaxDeviation: parseFloat(values.maxGap) / 100
      };

      const updatedThresholds =
        editingIndex !== null
          ? thresholds.map((t, i) => (i === editingIndex ? newThreshold : t))
          : [...thresholds, newThreshold];

      setThresholds(updatedThresholds);
      setEditingIndex(null);

      updateConversionThresholds.mutate(updatedThresholds, {
        onSuccess: () => {
          getPublisherSettings.refetch();
          resetForm();
        }
      });
    }
  });

  const handleEditThreshold = (index: number) => {
    const thresholdToEdit = thresholds[index];
    if (thresholdToEdit) {
      setValues({
        minRate:
          index > 0 ? thresholds[index - 1].priceThreshold.toFixed(0) : '0',
        maxRate: thresholdToEdit.priceThreshold.toFixed(0),
        minGap: (thresholdToEdit.negativeMaxDeviation * 100 * -1).toFixed(0),
        maxGap: (thresholdToEdit.positiveMaxDeviation * 100).toFixed(0)
      });
      setEditingIndex(index);
    }
  };

  const handleDeleteThreshold = (index: number) => {
    const updatedThresholds = thresholds.filter((_, i) => i !== index);
    setThresholds(updatedThresholds);

    updateConversionThresholds.mutate(updatedThresholds, {
      onSuccess: () => {
        getPublisherSettings.refetch();
      }
    });
  };

  const handleMaxRateBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (!values.maxRate) {
      handleBlur(e);
      return;
    }

    const filteredThresholds =
      editingIndex !== null
        ? thresholds.filter((_, i) => i !== editingIndex)
        : thresholds;

    const index = filteredThresholds.findIndex(
      ({ priceThreshold }) => Number(values.maxRate) <= Number(priceThreshold)
    );

    if (index >= 0) {
      const previousRowEndValue =
        index > 0 ? Number(filteredThresholds[index - 1].priceThreshold) : 0;

      setFieldValue('minRate', previousRowEndValue.toFixed());
    }

    handleBlur(e);
  };

  const columns = [
    {
      field: 'priceRangeStart',
      flex: 1,
      headerName: 'Price range start',
      renderCell: (params: GridRenderCellParams) => `$${params.value}`
    },
    {
      field: 'priceThreshold',
      flex: 1,
      headerName: 'Price range end',
      renderCell: (params: GridRenderCellParams) => `$${params.value}`
    },
    {
      field: 'negativeMaxDeviation',
      flex: 1,
      headerName: 'Min threshold',
      renderCell: (params: GridRenderCellParams) => `-${params.value}%`
    },
    {
      field: 'positiveMaxDeviation',
      flex: 1,
      headerName: 'Max threshold',
      renderCell: (params: GridRenderCellParams) => `${params.value}%`
    },
    {
      field: 'actions',
      flex: 0.5,
      type: 'actions',
      disableReorder: true,
      getActions: (params: GridRowParams) => {
        const isMaxThreshold = params.row.priceThreshold === MAX_USD_PRICE;

        return [
          <GridActionsCellItem
            icon={<EditTwoToneIcon />}
            label="Edit"
            onClick={() => handleEditThreshold(params.row._id)}
            disabled={
              !permissionsUtil.isActionEnabled(
                null,
                EPermissionGroup.PRICING,
                EPermissionAction.MANAGE
              )
            }
            showInMenu
          />,
          <GridActionsCellItem
            className="danger"
            icon={<DeleteOutlineTwoToneIcon className="danger" />}
            label="Delete"
            onClick={() => handleDeleteThreshold(params.row._id)}
            disabled={
              isMaxThreshold ||
              !permissionsUtil.canUserEdit() ||
              !permissionsUtil.isActionEnabled(
                null,
                EPermissionGroup.PRICING,
                EPermissionAction.MANAGE
              )
            }
            showInMenu
          />
        ];
      }
    }
  ];

  return (
    <Box className="settings-card mt-4 flex flex-row items-center justify-between p-9">
      <Stack width={'100%'}>
        <h3 className="text-base-cardForeground font-semibold">
          Create Price Guards
        </h3>
        <Box my={1.5}>
          <Typography
            fontSize="12px"
            color="#717188"
            maxWidth={'50%'}
            mt={1}
          >
            Create price guards to limit how much local prices can change from
            USD prices when exchange rates fluctuate. Enter a USD price range,
            and set percentage thresholds to control how much the local price
            can increase or decrease.
          </Typography>
        </Box>
        <Stack direction="column" spacing={2}>
          <Stack direction="row" spacing={2} mt={2}>
            <AcInput
              value={values.minRate}
              type="number"
              name="minRate"
              header="Price range start"
              disabled
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  )
                }
              }}
              sx={{
                opacity: 0.5
              }}
            />
            <AcInput
              value={values.maxRate}
              disabled={values.maxRate === MAX_USD_PRICE.toString()}
              type="number"
              name="maxRate"
              header="Price range end"
              onChange={handleChange}
              onBlur={handleMaxRateBlur}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  )
                }
              }}
              error={touched.maxRate && Boolean(errors.maxRate)}
              helperText={(touched.maxRate && errors.maxRate) || ''}
            />
            <AcInput
              value={values.minGap}
              type="number"
              name="minGap"
              header="Negative range threshold"
              onChange={handleChange}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">%</InputAdornment>
                  )
                }
              }}
              onBlur={handleBlur}
              error={touched.minGap && Boolean(errors.minGap)}
              helperText={(touched.minGap && errors.minGap) || ''}
            />
            <AcInput
              value={values.maxGap}
              type="number"
              name="maxGap"
              header="Positive range threshold"
              onChange={handleChange}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">%</InputAdornment>
                  )
                }
              }}
              onBlur={handleBlur}
              error={touched.maxGap && Boolean(errors.maxGap)}
              helperText={(touched.maxGap && errors.maxGap) || ''}
            />
            <Button
              type="submit"
              variant="secondary"
              onClick={(event) => {
                event.preventDefault();
                handleSubmit();
              }}
              style={{
                height: '48px',
                width: '100px',
                marginTop: '22px'
              }}
              disabled={
                disabled ||
                !isValid ||
                !dirty ||
                thresholds.some(
                  (t, i) =>
                    i !== editingIndex &&
                    t.priceThreshold === parseFloat(values.maxRate)
                )
              }
            >
              {editingIndex !== null ? 'Update' : 'Add'}
            </Button>
            <Button
              onClick={(event) => {
                event.preventDefault();
                resetForm();
              }}
              variant="ghost"
              style={{
                height: '48px',
                width: '100px',
                marginTop: '22px'
              }}
              disabled={!dirty}
            >
              Cancel
            </Button>
          </Stack>
        </Stack>
        <DataTable
          hideFooter={false}
          columns={columns}
          rows={rows}
          loading={
            getPublisherSettings.isLoading || getPublisherSettings.isFetching
          }
          onNoData={
            <FirstActionModal
              headline="No thresholds yet"
              text="Currently there is no thresholds"
            />
          }
        />
      </Stack>
    </Box>
  );
};

export default ExchangeRateThreshold;
